import { inject, Injectable } from '@angular/core';
import { ApolloLink, WatchQueryFetchPolicy } from '@apollo/client';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { ServiceCategoryFactory, UsergroupFactory } from 'app/factories';
import { ApolloQueryResult, mapToFetchResult, mapToQueryResult } from 'app/graphql/apollo-query-result';
import {
    AllServiceCategoriesQuery,
    BusinessPackageUsergroupInfoQuery,
    BusinessPackageUsergroupInfoQueryGQL,
    BusinessPackageUsergroupInfoQueryVariables,
    BusinessProfileOnboardingChecklistQuery,
    BusinessProfileOnboardingChecklistQueryGQL,
    CampaignsQuery,
    CompanyRegistryInfoQuery,
    SelectBusinessPackageMutation,
    SelectBusinessPackageMutationVariables,
    SignAgreement,
    StoreUsergroupMutation,
    StoreUsergroupMutationGQL,
    StoreUsergroupMutationVariables,
    UpdateUsergroupMutation,
    UpdateUsergroupMutationVariables,
    UsergroupAccountsQuery,
    UsergroupAllowedTaxExceptions,
    UsergroupBusinessPackagesQuery,
    UsergroupBusinessPackagesQueryGQL,
    UsergroupBusinessPackagesQueryVariables,
    UsergroupIncomeInfoQuery,
    UsergroupQuery,
    UsergroupRelevantTaxExceptions,
    UsergroupTypesQuery,
} from 'app/graphql/generated/graphql-types';
import { MeQueryDocument } from 'app/graphql/generated/types-user';
import { QueryResult, UsergroupTypeEnum } from 'app/interfaces';

@Injectable()
export class UsergroupService {
    private apollo = inject(Apollo);
    private serviceCategoryFactory = inject(ServiceCategoryFactory);
    private usergroupFactory = inject(UsergroupFactory);

    getAccounts(usergroupId: string | Observable<string>): Observable<QueryResult<UsergroupAccountsQuery>> {
        return this.usergroupFactory.getAccounts(usergroupId);
    }

    getAllowedTaxExceptions(
        usergroupId: string | Observable<string>,
    ): Observable<QueryResult<UsergroupAllowedTaxExceptions>> {
        return this.usergroupFactory.getAllowedTaxExceptions(usergroupId);
    }

    getBusinessAccounts(usergroupId: string | Observable<string>): Observable<QueryResult<UsergroupAccountsQuery>> {
        return this.usergroupFactory.getBusinessAccounts(usergroupId);
    }

    getBusinessPackage(
        variables: BusinessPackageUsergroupInfoQueryVariables,
    ): Observable<ApolloQueryResult<BusinessPackageUsergroupInfoQuery>> {
        return new BusinessPackageUsergroupInfoQueryGQL(this.apollo)
            .fetch({
                variables: variables,
                fetchPolicy: 'network-only',
            })
            .pipe(mapToFetchResult());
    }

    getBusinessPackages(
        variables: UsergroupBusinessPackagesQueryVariables,
    ): Observable<ApolloQueryResult<UsergroupBusinessPackagesQuery>> {
        return new UsergroupBusinessPackagesQueryGQL(this.apollo)
            .watch({
                variables: variables,
                fetchPolicy: 'network-only',
            })
            .valueChanges.pipe(mapToQueryResult());
    }

    getCampaigns(
        usergroupId?: string | Observable<string>,
        openForRegistration?: boolean,
        types?: string[],
    ): Observable<QueryResult<CampaignsQuery>> {
        return this.usergroupFactory.getCampaigns({ usergroupId, openForRegistration, types });
    }

    getCompanyRegistryInfo(name: string | Observable<string>): Observable<QueryResult<CompanyRegistryInfoQuery>> {
        return this.usergroupFactory.getCompanyRegistryInfo(name);
    }

    getIncomeInfo(usergroupId: string | Observable<string>): Observable<QueryResult<UsergroupIncomeInfoQuery>> {
        return this.usergroupFactory.getIncomeInfo(usergroupId);
    }

    getRelevantTaxExceptions(
        usergroupId: string | Observable<string>,
    ): Observable<QueryResult<UsergroupRelevantTaxExceptions>> {
        return this.usergroupFactory.getRelevantTaxExceptions(usergroupId);
    }

    getServiceCategories(): Observable<QueryResult<AllServiceCategoriesQuery>> {
        return this.serviceCategoryFactory.getAll();
    }

    getTypes(usergroupId: string | Observable<string>): Observable<UsergroupTypeEnum[]> {
        return this.usergroupFactory.getUsergroupTypes(usergroupId).pipe(map(this.usergroupTypeMapper.bind(this)));
    }

    getUsergroup(
        usergroupHash: string | Observable<string>,
        fetchPolicy?: WatchQueryFetchPolicy,
    ): Observable<ApolloQueryResult<UsergroupQuery>> {
        return this.usergroupFactory.getUsergroup({ usergroupHash }, fetchPolicy);
    }

    selectBusinessPackage(
        usergroupId: string,
        packageId: number,
        withdrawalInfo: Omit<SelectBusinessPackageMutationVariables, 'usergroupId' | 'packageId'>,
    ): Observable<ApolloLink.Result<SelectBusinessPackageMutation>> {
        return this.usergroupFactory.selectBusinessPackage({ ...withdrawalInfo, usergroupId, packageId });
    }

    storeUsergroup(variables: StoreUsergroupMutationVariables): Observable<ApolloLink.Result<StoreUsergroupMutation>> {
        return new StoreUsergroupMutationGQL(this.apollo).mutate({
            variables: variables,
            refetchQueries: [MeQueryDocument],
        });
    }

    private usergroupQueryCompletedSource = new BehaviorSubject<boolean>(false);
    usergroupQueryCompleted$ = this.usergroupQueryCompletedSource.asObservable();

    notifyUsergroupQueryCompletion() {
        this.usergroupQueryCompletedSource.next(true);
        this.usergroupQueryCompletedSource.next(false);
    }

    updateUsergroup(
        variables: UpdateUsergroupMutationVariables,
    ): Observable<ApolloLink.Result<UpdateUsergroupMutation>> {
        return this.usergroupFactory
            .updateUsergroup(variables)
            .pipe(finalize(() => this.notifyUsergroupQueryCompletion()));
    }

    signAgreement(
        usergroupId: string,
        userHash: string,
        licenseId: number,
    ): Observable<ApolloLink.Result<SignAgreement>> {
        return this.usergroupFactory.signAgreement({ usergroupId, userHash, licenseId });
    }

    private usergroupTypeMapper({ data }: QueryResult<UsergroupTypesQuery>): UsergroupTypeEnum[] {
        const types = [];
        if (!data?.usergroup) {
            return types;
        }

        if (data.usergroup.is_service_provider) {
            types.push(UsergroupTypeEnum.Serviceprovider);
        }

        if (data.usergroup.is_event_provider) {
            types.push(UsergroupTypeEnum.EventOrganizer);
        }

        if (data.usergroup.is_compensating) {
            types.push(UsergroupTypeEnum.Company);
        }
        return types;
    }

    fetchOnboardingChecklist(usergroupHash: string): Observable<QueryResult<BusinessProfileOnboardingChecklistQuery>> {
        return new BusinessProfileOnboardingChecklistQueryGQL(this.apollo)
            .fetch({
                variables: { usergroupHash },
                fetchPolicy: 'network-only',
            })
            .pipe(mapToFetchResult());
    }
}
